import { Icon, SkeletonDots } from '@yes.technology/react-toolkit'
import useDocumentLink from 'document/shared/hooks/useDocumentLink'
import { useMemo } from 'react'
import ImagePreview from './ImagePreview'
import { TableDocumentPreviewContainer } from './TableDocumentPreview.styles'
import type { TableDocumentPreviewProps } from './TableDocumentPreview.types'
import {
  getContentType,
  getMostRecentDocument,
  getURLContentType,
  isValidUrl,
  previewIcons
} from './TableDocumentPreview.utils'
import useTableContext from '../../hooks/useTableContext'

const TableDocumentPreview = ({
  documents,
  objectUuidOrIndex
}: TableDocumentPreviewProps) => {
  const { objects } = useTableContext()

  const mostRecentObject = useMemo(
    () => getMostRecentDocument(documents ?? []),
    [documents]
  )

  const documentToShow = useMemo(
    () =>
      objectUuidOrIndex
        ? objects.find((object) => object.uuid === objectUuidOrIndex)
        : mostRecentObject,
    [mostRecentObject, objectUuidOrIndex, objects]
  )

  const documentUuid = documentToShow?.uuid ?? ''

  const hasURLs =
    objectUuidOrIndex &&
    documentToShow?.url?.length &&
    documentToShow?.url?.length > 0

  const isValidURL = objectUuidOrIndex
    ? !!mostRecentObject?.des && isValidUrl(mostRecentObject?.des)
    : true

  const shouldSearchDocumentLink = (hasURLs && isValidURL) || !objectUuidOrIndex

  const { data: fileUrl, isLoading } = useDocumentLink({
    uuid: shouldSearchDocumentLink ? documentUuid : '',
    type: 'document'
  })

  if (isLoading)
    return (
      <TableDocumentPreviewContainer>
        <SkeletonDots size={3} />
      </TableDocumentPreviewContainer>
    )

  if (!documentToShow || !fileUrl) return null

  const contentType = objectUuidOrIndex
    ? getURLContentType(fileUrl)
    : getContentType(documentToShow)
  const previewIcon = previewIcons[contentType]

  return (
    <TableDocumentPreviewContainer>
      {contentType === 'image' ? (
        <a href={fileUrl} target='_blank' rel='noopener noreferrer'>
          <ImagePreview
            src={fileUrl}
            fallBackIcon={previewIcon}
            alt='Preview of the document'
          />
        </a>
      ) : (
        <a
          href={objectUuidOrIndex ? mostRecentObject?.des : fileUrl}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Icon iconName={previewIcon} icSize='large' icColor='primary' />
        </a>
      )}
    </TableDocumentPreviewContainer>
  )
}

export default TableDocumentPreview
